import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  Share as ShareIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardContent,
  Menu,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { collection, getDocs, query, where } from 'firebase/firestore';
import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppDialog, { DialogField } from '../components/AppDialog';
import ItemCardThumbHeader from '../components/ItemCardThumbHeader';
import MikiwameLoader from '../components/MikiwameLoader';
import WorkspaceShareDialog from '../components/WorkspaceShareDialog';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import { useGroups } from '../contexts/GroupContext';
import { useThemeMode } from '../contexts/ThemeContext';
import { useItemThumbnails } from '../hooks/useItemThumbnails';
import { useThemeColors } from '../hooks/useThemeColors';
import {
  deleteFactCheckItem,
  getPersonalItems,
  updateFactCheckItem,
  updateItemShare,
} from '../services/factCheckItemService';
import { takePrefetchedPersonalItems } from '../services/prefetch';
import { formatDuration } from '../services/videoUploadService';
import { IMG } from '../theme/assets';
import { verdictKeyFromLabel, verdictSealFromLabel } from '../theme/designTokens';
import type { FactCheckItem, FactCheckItemStatus, InvestigationItem } from '../types';
import { getStatusBadge } from '../types';

/** マイアイテムの状態チップ（mock準拠の淡色ピル）。getStatusBadge の color 名→{文字色, 背景}。 */
const STATUS_PILL: Record<string, { fg: string; bg: string }> = {
  green: { fg: '#16a34a', bg: 'rgba(22,163,74,0.13)' },
  blue: { fg: '#2563eb', bg: 'rgba(37,99,235,0.13)' },
  orange: { fg: '#e08600', bg: 'rgba(224,134,0,0.15)' },
  red: { fg: '#dc2626', bg: 'rgba(220,38,38,0.13)' },
  yellow: { fg: '#b45309', bg: 'rgba(180,83,9,0.15)' },
  gray: { fg: '#64748b', bg: 'rgba(100,116,139,0.14)' },
};

/** 「3つの方法でファクトチェックを始める」カード定義（mypage-action 準拠）。 */
const METHOD_CARDS = [
  {
    key: 'doc',
    owl: IMG.owl.methodDocument,
    title: '文書のファクトチェック',
    desc: '文書をアップロードして、含まれる主張を抽出・検証します。',
    btn: '始める',
    color: '#3b5bdb',
    tint: 'linear-gradient(160deg, #eef4ff, #ffffff)',
    path: '/create',
  },
  {
    key: 'vid',
    owl: IMG.owl.methodVideo,
    title: '動画のファクトチェック',
    desc: '動画をAIで文字起こしし、発言の事実性をタイムコード付きで検証。',
    btn: '始める',
    color: '#0d9488',
    tint: 'linear-gradient(160deg, #eef9f6, #ffffff)',
    path: '/create-video',
  },
  {
    key: 'dom',
    owl: IMG.owl.methodDomain,
    title: '特定のサイトに限定してファクトチェック',
    desc: '信頼できるドメインだけに絞って、根拠を照合しながら検証します。',
    btn: '始める',
    color: '#e0890c',
    tint: 'linear-gradient(160deg, #fff6ea, #ffffff)',
    path: '/domain-factcheck',
  },
] as const;

/** マイアイテムの状態フィルタ（mypage-action 準拠の michip）。 */
const STATUS_FILTERS: { key: FactCheckItemStatus | 'all' | 'uploading'; label: string }[] = [
  { key: 'all', label: 'すべて' },
  { key: 'uploading', label: 'アップロード中' },
  { key: 'extracted', label: '抽出レビュー' },
  { key: 'verifying', label: '検証中' },
  { key: 'completed', label: '完了' },
];

// ホームだけ全体を少し引いて見せる。zoom は入れ子で掛け算になるので、
// 全体の --ui-scale(=1) にこの値が乗る。vh 由来の高さはこのぶん割り戻す必要がある。
const HOME_SCALE = 0.9;
const homeHeight = `calc((var(--app-vh) - 64px - 64px) / ${HOME_SCALE})`;

const MyPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { groups } = useGroups();
  const { themeMode } = useThemeMode();
  const tokens = useThemeColors();

  // テーマに応じた色定義
  const textPrimary = themeMode === 'dark' ? '#fff' : '#1a1a2e';
  const textSecondary = themeMode === 'dark' ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)';
  const textMuted = themeMode === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)';
  const borderColor = themeMode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)';
  const cardBg =
    themeMode === 'dark'
      ? 'linear-gradient(135deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.005) 100%)'
      : 'linear-gradient(135deg, rgba(255,255,255,0.8) 0%, rgba(255,255,255,0.6) 100%)';
  const cardBorder = themeMode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.08)';
  // アクセントカラー（ティール系）
  const accentColor = themeMode === 'dark' ? '#64ffda' : '#00897b';
  const accentColorLight =
    themeMode === 'dark' ? 'rgba(100, 255, 218, 0.2)' : 'rgba(0, 137, 123, 0.15)';
  const accentColorBorder =
    themeMode === 'dark' ? 'rgba(100, 255, 218, 0.5)' : 'rgba(0, 137, 123, 0.5)';
  const [items, setItems] = useState<FactCheckItem[]>([]);
  const [filteredItems, setFilteredItems] = useState<FactCheckItem[]>([]);
  const itemThumbs = useItemThumbnails(filteredItems.map((i) => i.id));
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<FactCheckItemStatus | 'all' | 'uploading'>(
    'all',
  );
  const [loading, setLoading] = useState(true);
  const [investigationItems, setInvestigationItems] = useState<InvestigationItem[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);

  // 編集関連の状態
  const [editingItem, setEditingItem] = useState<FactCheckItem | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [saving, setSaving] = useState(false);

  // 案件共有関連の状態
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [shareItemId, setShareItemId] = useState<string | null>(null);
  // 何を見せるか。既定はファクトチェックのみ（規程チェックは判断を含むので確認してから出す）
  const [shareFactCheck, setShareFactCheck] = useState(true);
  const [shareCompliance, setShareCompliance] = useState(false);
  const loadItems = useCallback(async () => {
    if (!user) return;

    try {
      setLoading(true);
      // 認証チェックの裏で走らせた取得があればそれを待つ（無ければ通常どおり取りに行く）
      const personalItems = await (takePrefetchedPersonalItems(user.uid) ?? getPersonalItems());
      setItems(personalItems);
    } catch (err) {
      console.error('Error loading items:', err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  const loadInvestigationItems = useCallback(async () => {
    if (!user) return;

    try {
      const itemsQuery = query(
        collection(db, 'investigation_items'),
        where('userId', '==', user.uid),
      );

      const querySnapshot = await getDocs(itemsQuery);
      const items: InvestigationItem[] = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        items.push({
          id: data.id,
          userId: data.userId,
          project_id: data.project_id,
          project_title: data.project_title,
          fact_index: data.fact_index,
          claim: data.claim,
          verification_result: data.verification_result,
          confidence: data.confidence,
          added_at: data.added_at,
          status: data.status,
          updated_at: data.updated_at,
          investigation_started_at: data.investigation_started_at,
          title: data.title,
          messages: data.messages || [],
          message_count: data.message_count || 0,
          verification_summary: data.verification_summary || '',
        });
      });

      // updated_atで降順ソート
      items.sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());
      setInvestigationItems(items);
    } catch (err) {
      console.error('Error loading investigation items:', err);
    }
  }, [user?.uid, user]);

  // データの読み込み
  useEffect(() => {
    if (user) {
      loadItems();
      loadInvestigationItems();
    }
  }, [user, loadItems, loadInvestigationItems]);

  // 検索とフィルターの適用
  useEffect(() => {
    let filtered = items;

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (item) =>
          item.title.toLowerCase().includes(query) ||
          item.description?.toLowerCase().includes(query) ||
          item.fileName?.toLowerCase().includes(query),
      );
    }

    if (statusFilter !== 'all') {
      if (statusFilter === 'uploading') {
        // アップロード中はprocessingStageで判定
        filtered = filtered.filter((item) => item.processingStage === 'uploading');
      } else {
        filtered = filtered.filter((item) => item.status === statusFilter);
      }
    }

    setFilteredItems(filtered);
  }, [items, searchQuery, statusFilter]);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, itemId: string) => {
    setAnchorEl(event.currentTarget);
    setSelectedItemId(itemId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedItemId(null);
  };

  const handleDeleteItem = async () => {
    if (selectedItemId) {
      try {
        await deleteFactCheckItem(selectedItemId);
        await loadItems();
      } catch (err) {
        console.error('Error deleting item:', err);
      }
    }
    handleMenuClose();
  };

  const handleEditItem = () => {
    if (selectedItemId) {
      const item = items.find((i) => i.id === selectedItemId);
      if (item) {
        setEditingItem(item);
        setEditTitle(item.title);
        setEditDescription(item.description || '');
        setEditDialogOpen(true);
      }
    }
    handleMenuClose();
  };

  const handleEditSave = async () => {
    if (!editingItem) return;

    try {
      setSaving(true);
      await updateFactCheckItem(editingItem.id, {
        title: editTitle.trim(),
        description: editDescription.trim(),
      });

      setItems((prev) =>
        prev.map((i) =>
          i.id === editingItem.id
            ? { ...i, title: editTitle.trim(), description: editDescription.trim() }
            : i,
        ),
      );

      setEditDialogOpen(false);
      setEditingItem(null);
    } catch (err) {
      console.error('Edit save error:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleEditCancel = () => {
    setEditDialogOpen(false);
    setEditingItem(null);
    setEditTitle('');
    setEditDescription('');
  };

  // 案件共有関連
  const handleShareItem = () => {
    if (groups.length === 0) {
      alert('所属するグループがありません。');
      handleMenuClose();
      return;
    }
    // 共有対象のアイテムIDを保存（handleMenuCloseでselectedItemIdがnullになるため）
    setShareItemId(selectedItemId);
    setShareDialogOpen(true);
    handleMenuClose();
  };

  const handleShareDialogClose = () => {
    if (sharing) return;
    setShareDialogOpen(false);
    setShareItemId(null);
    setShareFactCheck(true);
    setShareCompliance(false);
  };

  const handleShareToWorkspace = async (workspaceId: string) => {
    if (!shareItemId) return;

    try {
      setSharing(true);
      await updateItemShare(shareItemId, workspaceId, {
        factCheck: shareFactCheck,
        compliance: shareCompliance,
      });
      // アイテムは自分のものとして残るので、一覧から消さない。
      // 共有先だけ手元の状態に反映する。
      setItems((prev) =>
        prev.map((i) =>
          i.id === shareItemId
            ? {
                ...i,
                sharedWorkspaceIds: Array.from(
                  new Set([...(i.sharedWorkspaceIds ?? []), workspaceId]),
                ),
                shares: {
                  ...(i.shares ?? {}),
                  [workspaceId]: { factCheck: shareFactCheck, compliance: shareCompliance },
                },
              }
            : i,
        ),
      );
      setShareDialogOpen(false);
      setShareItemId(null);
      setShareFactCheck(true);
      setShareCompliance(false);
    } catch (err) {
      console.error('Error sharing item to workspace:', err);
    } finally {
      setSharing(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // ステータスに応じた遷移先を決定（要件定義に準拠）
  const getNavigationPath = (item: FactCheckItem): string | null => {
    // 動画アイテムで処理中の場合は処理画面へ
    if (
      item.type === 'video_upload' &&
      item.processingStage &&
      item.processingStage !== 'upload_interrupted'
    ) {
      return `/video/processing/${item.id}`;
    }

    // ドキュメント(スキャンPDF)のOCR処理中(extracting)は進捗画面へ。
    // テキストPDFは同期で 'extracted' になるため、ここに該当するのは非同期OCR中のみ。
    const isVideo = item.type === 'video_upload' || item.type === 'video_url';
    if (!isVideo && item.status === 'extracting') {
      return `/document/processing/${item.id}`;
    }

    switch (item.status) {
      case 'extracted':
        // 抽出完了 → ファクト選択画面
        return `/workflow/${item.id}`;
      case 'verifying':
        // 検証中 → 処理中画面
        return `/processing/${item.id}`;
      case 'completed':
      case 'error':
        // 完了・エラー → 結果詳細画面
        return `/results/${item.id}`;
      default:
        // 抽出中 → アイテム詳細
        return `/items/${item.id}`;
    }
  };

  // 動画アイテムが遷移可能かどうか
  const isNavigable = (item: FactCheckItem): boolean => {
    // 動画処理中も遷移可能になったため、getNavigationPathの結果で判定
    return getNavigationPath(item) !== null;
  };

  // 読み込み待ち。灰色のスケルトンが画面いっぱいに並ぶと崩れて見えたので、
  // ブランドのローダー1つに置き換えた（取得自体は認証チェックの裏で先行して走っている）。
  if (loading) {
    return (
      <Box
        sx={{
          px: 4,
          zoom: HOME_SCALE,
          height: homeHeight,
          display: 'flex',
        }}
      >
        <MikiwameLoader label="アイテムを読み込んでいます" size={168} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        px: 4,
        zoom: HOME_SCALE, // ホームだけ引きで見せる（情報量が多く、等倍だと大きすぎるため）
        height: homeHeight, // AppBar(mt:8=64px) + Layout padding(p:4=32px*2=64px) を引いて縮尺で割り戻す
        bgcolor: 'transparent', // 背景はLayout側に任せる
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        overflowX: 'hidden',
        overflowY: 'auto',
      }}
    >
      <Box
        sx={{
          maxWidth: 1600,
          mx: 'auto',
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          width: '100%',
        }}
      >
        {/* ヒーロー（mypage-action ライト版を忠実再現） */}
        <Box
          sx={{
            position: 'relative',
            mt: 2,
            mb: 3,
            px: 5,
            py: '12px',
            minHeight: 78,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: { xs: 3, sm: 6, md: 12 },
            borderRadius: '16px',
            overflow: 'hidden',
            background:
              themeMode === 'dark'
                ? `linear-gradient(120deg, ${tokens.heroFrom}, ${tokens.heroTo})`
                : `url(${IMG.heroBg.light}) center / cover no-repeat`,
            boxShadow: '0 16px 36px rgba(60,60,140,0.13)',
            border: themeMode === 'dark' ? `1px solid ${tokens.line}` : 'none',
          }}
        >
          <Box
            sx={{
              position: 'relative',
              zIndex: 2,
              flex: '0 0 auto',
              maxWidth: { xs: 520, md: 'none' },
              minWidth: 0,
            }}
          >
            <Typography
              sx={{
                fontSize: 12.5,
                fontWeight: 800,
                color: themeMode === 'dark' ? tokens.indigo : '#3b5bdb',
              }}
            >
              こんにちは{user?.displayName ? `、${user.displayName}さん` : ''}
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: 20, sm: 25 },
                fontWeight: 900,
                letterSpacing: '-0.02em',
                lineHeight: 1.15,
                m: '4px 0 6px',
                color: themeMode === 'dark' ? tokens.ink : '#26324b',
              }}
            >
              その主張、根拠から見極める。
            </Typography>
            <Typography
              sx={{
                fontSize: 13.5,
                lineHeight: 1.5,
                color: themeMode === 'dark' ? tokens.muted : '#26324b',
                opacity: themeMode === 'dark' ? 1 : 0.92,
                whiteSpace: { xs: 'normal', md: 'nowrap' },
              }}
            >
              文書・動画・指定サイトの中から事実に関する主張を自動で抽出し、信頼できる情報源と照らし合わせながら、AIが根拠つきで一つひとつ丁寧に検証します。
            </Typography>
          </Box>
          <Box
            component="img"
            src={IMG.owl.hero}
            alt=""
            sx={{
              position: 'relative',
              zIndex: 2,
              height: 108,
              width: 'auto',
              flexShrink: 0,
              filter: 'drop-shadow(0 12px 18px rgba(0,0,0,0.2))',
            }}
          />
        </Box>

        {/* 検証導線セクション（mypage-action 準拠） */}
        <Box sx={{ mb: 3, mt: 0, pt: 1 }}>
          <Typography sx={{ fontSize: 19, fontWeight: 900, color: textPrimary, lineHeight: 1.2 }}>
            3つの方法でファクトチェックを始める
          </Typography>
          <Typography sx={{ fontSize: 13, color: textMuted, mt: 0.25, mb: 2 }}>
            用途に合わせて選んでください
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
              gap: 2.5,
            }}
          >
            {METHOD_CARDS.map((card) => (
              <Box
                key={card.key}
                onClick={() => navigate(card.path)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.75,
                  px: 2.25,
                  py: 1.5,
                  borderRadius: '14px',
                  border: `1px solid ${tokens.line}`,
                  background: themeMode === 'dark' ? 'rgba(255,255,255,0.04)' : card.tint,
                  boxShadow: '0 10px 30px rgba(30,30,70,0.08)',
                  cursor: 'pointer',
                  transition: 'transform .22s ease, box-shadow .22s ease',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 20px 40px rgba(30,30,70,0.16)',
                  },
                }}
              >
                <Box
                  component="img"
                  src={card.owl}
                  alt=""
                  sx={{
                    width: 72,
                    height: 72,
                    objectFit: 'contain',
                    flexShrink: 0,
                    filter: 'drop-shadow(0 8px 12px rgba(30,30,70,0.12))',
                  }}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontSize: 16,
                      fontWeight: 900,
                      lineHeight: 1.2,
                      color: themeMode === 'dark' ? tokens.ink : '#1b2030',
                    }}
                  >
                    {card.title}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 11.5,
                      lineHeight: 1.45,
                      color: themeMode === 'dark' ? tokens.muted : '#5c6275',
                      mt: 0.5,
                      mb: 1.25,
                    }}
                  >
                    {card.desc}
                  </Typography>
                  <Button
                    disableElevation
                    variant="contained"
                    endIcon={
                      <Box component="span" sx={{ fontSize: 16, lineHeight: 1, ml: -0.25 }}>
                        →
                      </Box>
                    }
                    sx={{
                      background: card.color,
                      color: '#fff',
                      fontWeight: 800,
                      fontSize: 13,
                      borderRadius: '11px',
                      px: 1.75,
                      py: 0.9,
                      textTransform: 'none',
                      boxShadow: 'none',
                      '&:hover': {
                        background: card.color,
                        filter: 'brightness(0.95)',
                        boxShadow: 'none',
                      },
                    }}
                  >
                    {card.btn}
                  </Button>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Main Content Area - Two Column Layout */}
        <Box
          sx={{
            display: 'flex',
            gap: 5,
            flexDirection: { xs: 'column', lg: 'row' },
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
          }}
        >
          {/* Left: Items List (2/3) */}
          <Box
            sx={{
              flex: { xs: 1, lg: 2 },
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            {/* Header with Search */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                mb: 3,
                flexWrap: 'wrap',
              }}
            >
              <Typography
                sx={{
                  fontSize: 19,
                  fontWeight: 900,
                  color: textPrimary,
                  lineHeight: 1.2,
                  flexShrink: 0,
                }}
              >
                マイアイテム
              </Typography>

              {/* Status Filter（マイアイテム見出しの横・mypage-action の michip） */}
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {STATUS_FILTERS.map((f) => {
                  const on = statusFilter === f.key;
                  return (
                    <Box
                      key={f.key}
                      component="button"
                      onClick={() => setStatusFilter(f.key)}
                      sx={{
                        fontFamily: 'inherit',
                        fontSize: 12,
                        fontWeight: 700,
                        px: 1.6,
                        py: 0.75,
                        borderRadius: 999,
                        border: `1px solid ${on ? 'transparent' : tokens.line}`,
                        bgcolor: on
                          ? tokens.teal
                          : themeMode === 'dark'
                            ? 'rgba(255,255,255,0.04)'
                            : 'rgba(255,255,255,0.7)',
                        color: on ? '#fff' : tokens.muted,
                        cursor: 'pointer',
                        transition: '0.15s',
                        '&:hover': on ? {} : { borderColor: tokens.teal, color: tokens.teal },
                      }}
                    >
                      {f.label}
                    </Box>
                  );
                })}
              </Box>

              <TextField
                placeholder="アイテム検索..."
                size="small"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                slotProps={{
                  input: {
                    startAdornment: <SearchIcon sx={{ color: textMuted, mr: 1 }} />,
                  },
                }}
                sx={{
                  width: 250,
                  ml: 'auto',
                  '& .MuiOutlinedInput-root': {
                    backgroundColor:
                      themeMode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                    backdropFilter: 'blur(10px)',
                    borderRadius: 3,
                    color: textPrimary,
                    '& fieldset': { borderColor: borderColor },
                    '&:hover fieldset': {
                      borderColor:
                        themeMode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
                    },
                    '&.Mui-focused fieldset': { borderColor: accentColorBorder },
                  },
                  '& .MuiInputBase-input::placeholder': { color: textMuted, opacity: 1 },
                }}
              />
            </Box>

            {/* Items Grid - スクロール可能エリア */}
            <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0, mx: -1, px: 1 }}>
              {filteredItems.length === 0 ? (
                <Box
                  sx={{
                    textAlign: 'center',
                    color: tokens.faint,
                    fontSize: 13,
                    py: 6,
                    px: 3,
                  }}
                >
                  {searchQuery || statusFilter !== 'all'
                    ? '該当するアイテムがありません'
                    : 'まだアイテムがありません。上のカードから新規作成を始めましょう。'}
                </Box>
              ) : (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      sm: 'repeat(2, 1fr)',
                      xl: 'repeat(3, 1fr)',
                    },
                    gap: 3,
                    pt: 1,
                    pb: 1,
                  }}
                >
                  {filteredItems.map((item) => (
                    <Card
                      key={item.id}
                      onClick={() => {
                        const path = getNavigationPath(item);
                        if (path) navigate(path);
                      }}
                      sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        position: 'relative',
                        cursor: isNavigable(item) ? 'pointer' : 'default',
                        background: cardBg,
                        backdropFilter: 'blur(8px)',
                        border: `1px solid ${cardBorder}`,
                        borderRadius: 2,
                        overflow: 'hidden',
                        boxShadow:
                          themeMode === 'dark'
                            ? '0 8px 32px rgba(0,0,0,0.1)'
                            : '0 4px 20px rgba(0,0,0,0.08)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow:
                            themeMode === 'dark'
                              ? '0 12px 40px rgba(0,0,0,0.2)'
                              : '0 8px 30px rgba(0,0,0,0.12)',
                          border: `1px solid ${accentColorBorder}`,
                          zIndex: 1,
                        },
                      }}
                    >
                      <ItemCardThumbHeader
                        thumb={itemThumbs[item.id]}
                        isVideo={item.type === 'video_upload' || item.type === 'video_url'}
                        title={item.title}
                        onMenu={(e) => handleMenuOpen(e, item.id)}
                      />
                      <CardContent sx={{ flexGrow: 1, p: 2 }}>
                        {/* 説明 */}
                        <Typography
                          sx={{
                            fontSize: 12,
                            color: item.description ? textSecondary : textMuted,
                            mb: 1.5,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {item.description || '説明なし'}
                        </Typography>

                        {/* 状態チップ + 日付 */}
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            mb: 1.5,
                          }}
                        >
                          {(() => {
                            const badge = getStatusBadge(item);
                            const pill = STATUS_PILL[badge.color] ?? STATUS_PILL.gray;
                            return (
                              <Box
                                component="span"
                                sx={{
                                  fontSize: 10.5,
                                  fontWeight: 800,
                                  px: 1.1,
                                  py: '2px',
                                  borderRadius: 999,
                                  bgcolor: pill.bg,
                                  color: pill.fg,
                                }}
                              >
                                {badge.label}
                              </Box>
                            );
                          })()}
                          <Typography sx={{ fontSize: 11, color: textMuted }}>
                            {item.type === 'video_upload' && item.durationSec
                              ? formatDuration(item.durationSec)
                              : formatDate(item.updatedAt)}
                          </Typography>
                        </Box>

                        {/* 2カラム統計 */}
                        <Box
                          sx={{
                            display: 'flex',
                            gap: 2,
                            borderTop: `1px solid ${cardBorder}`,
                            pt: 1.5,
                          }}
                        >
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              sx={{
                                fontSize: 20,
                                fontWeight: 900,
                                lineHeight: 1,
                                color: textPrimary,
                              }}
                            >
                              {item.total_facts}
                            </Typography>
                            <Typography sx={{ fontSize: 10.5, color: textMuted, mt: 0.5 }}>
                              見つかったチェック箇所
                            </Typography>
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              sx={{
                                fontSize: 20,
                                fontWeight: 900,
                                lineHeight: 1,
                                color: tokens.teal,
                              }}
                            >
                              {item.verified_facts}
                            </Typography>
                            <Typography sx={{ fontSize: 10.5, color: textMuted, mt: 0.5 }}>
                              チェックが済んだ箇所
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              )}
            </Box>
          </Box>

          {/* Right: AI Follow-up Investigation (1/3) */}
          <Box
            sx={{
              flex: { xs: 1, lg: 1 },
              minWidth: { lg: 360 },
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <Typography
              sx={{ fontSize: 19, fontWeight: 900, color: textPrimary, lineHeight: 1.2, mb: 1.5 }}
            >
              AIによる追加リサーチ
            </Typography>

            <Card
              sx={{
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                background: cardBg,
                backdropFilter: 'blur(10px)',
                border: `1px solid ${cardBorder}`,
                borderRadius: 2,
                boxShadow:
                  themeMode === 'dark'
                    ? '0 8px 32px rgba(0,0,0,0.1)'
                    : '0 4px 20px rgba(0,0,0,0.08)',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: -20,
                  right: -20,
                  width: 100,
                  height: 100,
                  borderRadius: '50%',
                  background: 'rgba(100, 255, 218, 0.08)',
                  filter: 'blur(40px)',
                  zIndex: -1,
                },
              }}
            >
              {/* グラデ見出し（owl） */}
              <Box
                sx={{
                  background:
                    themeMode === 'dark'
                      ? 'linear-gradient(135deg, rgba(102,126,234,0.20), rgba(13,148,136,0.16))'
                      : 'linear-gradient(135deg, #eef0ff, #e7f6ff)',
                  px: 2.25,
                  py: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                }}
              >
                <Box
                  component="img"
                  src={IMG.owl.followupPanel}
                  alt=""
                  sx={{
                    width: 52,
                    height: 52,
                    objectFit: 'contain',
                    flexShrink: 0,
                    filter: 'drop-shadow(0 8px 12px rgba(40,40,90,0.14))',
                  }}
                />
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 14.5, fontWeight: 900, color: textPrimary }}>
                    気になる主張を、さらに深く。
                  </Typography>
                  <Typography sx={{ fontSize: 11.5, color: textMuted, mt: 0.25, lineHeight: 1.5 }}>
                    検証済みの主張をAIが追加調査し、背景や根拠を掘り下げます。
                  </Typography>
                </Box>
                <Box
                  onClick={() => navigate('/investigation')}
                  sx={{
                    ml: 'auto',
                    alignSelf: 'flex-start',
                    flexShrink: 0,
                    fontSize: 12,
                    fontWeight: 800,
                    color: tokens.indigo,
                    whiteSpace: 'nowrap',
                    cursor: 'pointer',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  すべての調査を見る →
                </Box>
              </Box>
              <CardContent sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
                {investigationItems.length > 0 ? (
                  <>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      {investigationItems
                        .filter((item) => item.status === 'waiting')
                        .slice(0, 6)
                        .map((item) => (
                          <Card
                            key={item.id}
                            sx={{
                              cursor: 'pointer',
                              background:
                                themeMode === 'dark'
                                  ? 'rgba(255,255,255,0.03)'
                                  : 'rgba(0,0,0,0.02)',
                              border: `1px solid ${themeMode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`,
                              borderRadius: 2,
                              transition: 'all 0.2s',
                              '&:hover': {
                                background:
                                  themeMode === 'dark'
                                    ? 'rgba(255,255,255,0.06)'
                                    : 'rgba(0,0,0,0.04)',
                                border: `1px solid ${accentColorBorder}`,
                              },
                            }}
                            onClick={() => navigate(`/investigation/${item.id}`)}
                          >
                            <CardContent sx={{ py: 1.5, px: 1.75, '&:last-child': { pb: 1.5 } }}>
                              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
                                <Box
                                  component="img"
                                  src={verdictSealFromLabel(item.verification_result)}
                                  alt=""
                                  sx={{
                                    width: 34,
                                    height: 34,
                                    objectFit: 'contain',
                                    flexShrink: 0,
                                    mt: '1px',
                                    filter: 'drop-shadow(0 3px 5px rgba(30,30,70,0.12))',
                                  }}
                                />
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography
                                    sx={{
                                      fontSize: 13,
                                      fontWeight: 600,
                                      lineHeight: 1.5,
                                      color: textPrimary,
                                      display: '-webkit-box',
                                      WebkitLineClamp: 2,
                                      WebkitBoxOrient: 'vertical',
                                      overflow: 'hidden',
                                    }}
                                  >
                                    {item.claim}
                                  </Typography>
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 1,
                                      mt: 1,
                                      flexWrap: 'wrap',
                                    }}
                                  >
                                    {item.verification_result &&
                                      (() => {
                                        const v =
                                          tokens.verdict[
                                            verdictKeyFromLabel(item.verification_result)
                                          ];
                                        return (
                                          <Box
                                            component="span"
                                            sx={{
                                              fontSize: 10.5,
                                              fontWeight: 800,
                                              px: 1.1,
                                              py: '2px',
                                              borderRadius: 999,
                                              bgcolor: v.tint,
                                              color: v.color,
                                            }}
                                          >
                                            {item.verification_result}
                                          </Box>
                                        );
                                      })()}
                                    <Box
                                      component="span"
                                      sx={{
                                        fontSize: 10.5,
                                        fontWeight: 800,
                                        px: 1.1,
                                        py: '2px',
                                        borderRadius: 999,
                                        bgcolor:
                                          themeMode === 'dark'
                                            ? 'rgba(129,140,248,0.18)'
                                            : '#ede9ff',
                                        color: tokens.indigo,
                                      }}
                                    >
                                      {item.status === 'investigating' ? '調査中' : '待機'}
                                    </Box>
                                    <Typography
                                      component="span"
                                      sx={{
                                        fontSize: 11,
                                        color: textMuted,
                                        ml: 'auto',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        maxWidth: 130,
                                      }}
                                    >
                                      {item.project_title}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Box>
                            </CardContent>
                          </Card>
                        ))}

                      {investigationItems.filter((item) => item.status === 'waiting').length >
                        6 && (
                        <Card
                          sx={{
                            cursor: 'pointer',
                            background: accentColorLight,
                            border: `1px solid ${accentColorBorder}`,
                            borderRadius: 2,
                            '&:hover': {
                              background:
                                themeMode === 'dark'
                                  ? 'rgba(100, 255, 218, 0.1)'
                                  : 'rgba(0, 137, 123, 0.2)',
                            },
                          }}
                          onClick={() => navigate('/investigation')}
                        >
                          <CardContent sx={{ py: 1.5, px: 2, textAlign: 'center' }}>
                            <Typography variant="body2" sx={{ color: accentColor }}>
                              他{' '}
                              {investigationItems.filter((item) => item.status === 'waiting')
                                .length - 6}{' '}
                              件を見る...
                            </Typography>
                          </CardContent>
                        </Card>
                      )}
                    </Box>

                    {investigationItems.filter((item) => item.status === 'waiting').length ===
                      0 && (
                      <Typography
                        sx={{ fontStyle: 'italic', textAlign: 'center', py: 3, color: textMuted }}
                      >
                        待機中の調査対象がありません。
                        <br />
                        ファクトチェック結果から追加してください。
                      </Typography>
                    )}
                  </>
                ) : (
                  <Typography
                    sx={{ fontStyle: 'italic', textAlign: 'center', py: 4, color: textMuted }}
                  >
                    まだ調査対象がありません。
                    <br />
                    ファクトチェック結果から対象を追加してください。
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Box>
        </Box>
      </Box>

      {/* メニュー */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem
          onClick={() => {
            if (selectedItemId) {
              const item = items.find((i) => i.id === selectedItemId);
              if (item) {
                const path = getNavigationPath(item);
                if (path) navigate(path);
              }
            }
            handleMenuClose();
          }}
          disabled={(() => {
            if (!selectedItemId) return true;
            const item = items.find((i) => i.id === selectedItemId);
            return item ? !isNavigable(item) : true;
          })()}
        >
          <ViewIcon sx={{ mr: 1 }} />
          詳細を見る
        </MenuItem>
        <MenuItem onClick={handleEditItem}>
          <EditIcon sx={{ mr: 1 }} />
          編集
        </MenuItem>
        <MenuItem onClick={handleShareItem}>
          <ShareIcon sx={{ mr: 1 }} />
          案件に共有
        </MenuItem>
        <MenuItem onClick={handleDeleteItem} sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 1 }} />
          削除
        </MenuItem>
      </Menu>

      {/* 編集ダイアログ */}
      <AppDialog
        open={editDialogOpen}
        onClose={handleEditCancel}
        icon={<Box component="img" src={IMG.item.document} alt="" />}
        title="アイテムを編集"
        subtitle="タイトルと説明を変えます"
        actions={
          <>
            <Button onClick={handleEditCancel} disabled={saving} sx={{ textTransform: 'none' }}>
              キャンセル
            </Button>
            <Button
              onClick={handleEditSave}
              variant="contained"
              disabled={saving || !editTitle.trim()}
              sx={{ textTransform: 'none', fontWeight: 800, borderRadius: '10px', px: 2.5 }}
            >
              {saving ? '保存中...' : '保存'}
            </Button>
          </>
        }
      >
        <DialogField
          label="タイトル"
          placeholder="例: 0913_台本.docx"
          value={editTitle}
          onChange={setEditTitle}
          required
        />
        <DialogField
          label="説明"
          placeholder="例: 9/13放送分のスタジオ台本"
          value={editDescription}
          onChange={setEditDescription}
          multiline
          rows={3}
        />
      </AppDialog>

      {/* 案件共有ダイアログ。サイト限定の画面と同じ部品を使う。
          マイアイテムからのときだけ「見せる結果」の切り替えを足す */}
      <WorkspaceShareDialog
        open={shareDialogOpen}
        onClose={handleShareDialogClose}
        onShare={handleShareToWorkspace}
        title="案件に共有"
        canShare={shareFactCheck || shareCompliance}
        renderExtra={() => {
          const target = items.find((i) => i.id === shareItemId);
          const hasCompliance = !!target?.complianceCheckedAt;
          const row = (
            label: string,
            sub: string,
            on: boolean,
            set: (v: boolean) => void,
            disabled: boolean,
          ) => (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 1.75,
                py: 1.25,
                borderTop: `1px solid ${tokens.line}`,
                opacity: disabled ? 0.5 : 1,
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 800, color: tokens.ink }}>
                  {label}
                </Typography>
                <Typography sx={{ fontSize: 11, color: tokens.faint }}>{sub}</Typography>
              </Box>
              <Box sx={{ flex: 1 }} />
              <Switch
                checked={on && !disabled}
                disabled={disabled}
                onChange={(e) => set(e.target.checked)}
              />
            </Box>
          );
          return (
            <Box sx={{ mt: 1, border: `1px solid ${tokens.line}`, borderRadius: '11px' }}>
              <Typography
                sx={{
                  fontSize: 10,
                  fontWeight: 900,
                  letterSpacing: '.07em',
                  color: tokens.faint,
                  px: 1.75,
                  pt: 1.5,
                  pb: 0.5,
                }}
              >
                この案件に見せる結果
              </Typography>
              {row(
                'ファクトチェックの結果',
                '抽出した主張と、その判定',
                shareFactCheck,
                setShareFactCheck,
                false,
              )}
              {row(
                '規程チェックの結果',
                hasCompliance ? '放送基準に照らした指摘' : 'まだ規程チェックをしていません',
                shareCompliance,
                setShareCompliance,
                !hasCompliance,
              )}
            </Box>
          );
        }}
      />
    </Box>
  );
};

export default MyPage;
